import { useEffect, useMemo } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link, NavLink, Route, Routes } from 'react-router-dom'

import { RoundedWarningIcon } from '../../components/Icons'
import { useSubscriptionDetails } from '../../hooks/apiHooks/subscriptionHooks'
import { useUser } from '../../hooks/storeHooks/useUserState'
import { OidcUser } from '../../types/oidcUser'
import { getOrganizationId } from '../../utils/getOrganization'
import BillingTab from './components/BillingTab'
import OverviewTab from './components/OverviewTab'

export default function AccountPage() {
  const auth = useAuth()
  const { user, isAuthenticated } = auth
  const { user: storedUser, isLoading, updateUser, isUpdating } = useUser()
  const orgId = getOrganizationId(user as OidcUser)

  const {
    data: subscriptionDetails,
    isFetching,
    refetch,
    error: subscriptionError,
    isError,
  } = useSubscriptionDetails({ organization_id: orgId }, isAuthenticated)

  useEffect(() => {
    if (isError) {
      if (subscriptionError.status === 401) {
        void auth.signinRedirect({ state: { returnUrl: location.pathname } })
      }
    }
  }, [isError, subscriptionError])

  const handleSubmitForm = (data: { firstName: string; lastName: string }) => {
    if (isAuthenticated) {
      void updateUser(storedUser?.id || '', {
        firstName: data.firstName,
        lastName: data.lastName,
      })
    } else {
      void auth.signinRedirect({ state: { returnUrl: location.pathname } })
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Please sign in to view your account.</p>
      </div>
    )
  }

  const isUnsubscribed = useMemo(() => {
    if (isFetching) return false
    return (
      subscriptionDetails?.credit_usage.subscription_details.status !==
        'subscribed' &&
      subscriptionDetails?.billingDetails.paymentType !== 'Free'
    )
  }, [subscriptionDetails])

  const baseClasses = 'leading-[150%] rounded-md font-medium px-3 py-1'
  return (
    <div className="tc-mv-card-wrapper">
      <div className="min-h-screen bg-gray-900">
        <div
          className={`max-w-7xl mx-auto my-24  sm:px-6 px-3 lg:px-8 py-8 ${isUnsubscribed ? 'mt-14' : ''}`}
        >
          {isUnsubscribed && (
            <div
              data-testid="unsubscribed-account-warning"
              className="tc-mv-blur-red text-white text-left p-4 rounded-md mb-4 "
            >
              <RoundedWarningIcon className="inline align-text-top" />
              <span className="leading-[150%] ms-1">
                There is no active Napster subscription. Navigate to{' '}
                <Link to="/pricing" className="underline">
                  Billing
                </Link>{' '}
                page to learn more.
              </span>
            </div>
          )}
          <nav className="flex gap-2 sm:mb-8 mb-4 rounded-md ms-5">
            <NavLink
              to="/account"
              className={({ isActive }) =>
                `${baseClasses} ${
                  isActive ? 'bg-white text-black' : 'text-white'
                }`
              }
              end
            >
              Overview
            </NavLink>
            <NavLink
              to="/account/billing"
              className={({ isActive }) =>
                `${baseClasses} ${
                  isActive ? 'bg-white text-black' : 'text-white'
                }`
              }
            >
              Billing
            </NavLink>
          </nav>
          <Routes>
            <Route
              path="/"
              element={
                <OverviewTab
                  userFirstName={storedUser?.firstName}
                  userLastName={storedUser?.lastName}
                  email={storedUser?.email || ''}
                  subscriptionDetails={subscriptionDetails}
                  isFetching={isLoading || isFetching}
                  onSubmitForm={handleSubmitForm}
                  isUpdating={isUpdating}
                />
              }
            />
            <Route
              path="/billing"
              element={
                <BillingTab
                  subscriptionDetails={subscriptionDetails}
                  isFetching={isFetching}
                  refetch={() => {
                    void refetch()
                  }}
                />
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  )
}
